import { Input, NativeSelect, SimpleGrid, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import type { CustomHoliday } from "@/api/CustomHoliday";
import type { HolidayDateRule } from "@/api/HolidayDateRule";
import type { User } from "@/api/User";
import { Alert } from "@/components/ui/alert";
import { Field } from "@/components/ui/field";
import { DialogRoot, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter, DialogCloseTrigger } from "@/components/ui/dialog";
import DateSelectController from "@/components/fields/DateSelect";
import CancelButton from "@/components/buttons/CancelButton";
import SubmitButton from "@/components/buttons/SubmitButton";
import { formatIso8601 } from "@/data/DateTimeUtil";
import { useI18n } from "@/i18n/I18nContext";
import { holidayService } from "@/service/HolidayService";
import apiClient from "@/service/core/ApiClient";

export type HolidayAction = "create" | "edit" | "remove";

type Values = {
  name: string;
  type: string;
  date: string | null;
  month: string;
  day: string;
  weekday: string;
  ordinal: string;
  duration: string;
  from: string | null;
  until: string | null;
};

const validDate = (value: string | null): value is string => !!value &&
  /^\d{4}-\d{2}-\d{2}$/.test(value) && value >= "1900-01-01" && value <= "2500-12-31" &&
  !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;

export const CustomHolidayDialog = ({ action, rule, actor, onClose, onSaved, finalFocusEl }: {
  action: HolidayAction;
  rule?: CustomHoliday;
  actor: User;
  onClose: () => void;
  onSaved: () => void;
  finalFocusEl?: () => HTMLElement | null;
}) => {
  const { language, t } = useI18n();
  const [error, setError] = useState<string | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const started = !!rule && rule.localValidFrom <= today;
  const removing = action === "remove";
  const ruleTypes = ["once", "annual_date", "nth_weekday"];
  const { register, control, watch, handleSubmit, formState: { isSubmitting } } = useForm<Values>({
    defaultValues: {
      name: rule?.name ?? "",
      type: rule ? (ruleTypes.includes(rule.dateRule.type) ? rule.dateRule.type : "") : "once",
      date: rule?.dateRule.date ?? tomorrow,
      month: String(rule?.dateRule.month ?? 12),
      day: String(rule?.dateRule.day ?? 31),
      weekday: String(rule?.dateRule.weekday ?? 1),
      ordinal: String(rule?.dateRule.ordinal ?? 3),
      duration: String(rule?.lengthInDays ?? 1),
      from: rule && !started ? rule.localValidFrom : tomorrow,
      until: rule?.localValidUntil ?? null,
    },
  });
  const type = watch("type");
  const locale = language === "en" ? "en-GB" : "de-CH";
  const dateProps = {
    control,
    parseValue: (value: unknown) => value ? new Date(`${value}T00:00:00`) : undefined,
    serializeValue: (date: Date | null) => date ? formatIso8601(date) : null,
    minDate: new Date(`${tomorrow}T00:00:00`),
    maxDate: new Date(2500, 11, 31),
  };

  const submit = async (values: Values) => {
    setError(null);
    if (!removing && !ruleTypes.includes(values.type)) {
      setError(t("customHolidays.invalidRuleType"));
      return;
    }
    if (rule && ((started && rule.dateRule.type === "once") || (rule.localValidUntil && rule.localValidUntil <= today))) {
      setError(t("customHolidays.readOnly"));
      return;
    }
    if ((!removing || started) && (!validDate(values.from) || values.from <= today)) {
      setError(t("customHolidays.futureDate"));
      return;
    }
    if (started && rule?.localValidUntil && values.from! > rule.localValidUntil) {
      setError(t("customHolidays.effectiveWithinValidity"));
      return;
    }
    if (!removing && values.until && (!validDate(values.until) || values.until < values.from!)) {
      setError(t("customHolidays.invalidUntil"));
      return;
    }
    if (!removing && (!values.name.trim() || values.name.length > 200)) {
      setError(t("customHolidays.invalidName"));
      return;
    }
    const dateRule: HolidayDateRule = { type: values.type, date: null, month: null, day: null, weekday: null, ordinal: null };
    if (!removing) {
      if (values.type === "once") {
        if (!validDate(values.date) || values.date < values.from! || (!!values.until && values.date > values.until)) {
          setError(t("customHolidays.oneOffWithinValidity"));
          return;
        }
        dateRule.date = values.date;
      } else if (values.type === "annual_date") {
        const date = `2000-${values.month.padStart(2, "0")}-${values.day.padStart(2, "0")}`;
        if (!validDate(date)) {
          setError(t("customHolidays.invalidAnnualDate"));
          return;
        }
        dateRule.month = Number(values.month);
        dateRule.day = Number(values.day);
      } else if (values.type === "nth_weekday") {
        dateRule.month = Number(values.month);
        dateRule.weekday = Number(values.weekday);
        dateRule.ordinal = Number(values.ordinal);
      }
    }
    try {
      if (removing) {
        await holidayService.removeCustom(rule!.id, started ? values.from! : undefined);
      } else {
        const now = new Date();
        const entry: CustomHoliday = {
          id: -1, organizationId: actor.organizationId, createdAt: now, createdBy: actor.id, updatedAt: now, updatedBy: actor.id,
          ...rule,
          name: values.name.trim(), dateRule, lengthInDays: Number(values.duration),
          localValidFrom: values.from!, localValidUntil: values.until || null,
        };
        if (action === "create") await holidayService.createCustom(entry);
        else await holidayService.updateCustom(entry);
      }
      onSaved();
      onClose();
    } catch (reason) {
      try { await apiClient.handleError(reason); }
      catch (failure) { setError(failure instanceof Error ? failure.message : t("customHolidays.writeError")); }
    }
  };

  return <DialogRoot open lazyMount placement="center" size="lg" scrollBehavior="inside"
    finalFocusEl={finalFocusEl}
    closeOnEscape={!isSubmitting} closeOnInteractOutside={!isSubmitting}
    onOpenChange={({ open }) => { if (!open && !isSubmitting) onClose(); }}>
    <DialogContent>
      <DialogHeader><DialogTitle>{t(`customHolidays.${removing && started ? "endTitle" : `${action}Title`}`)}</DialogTitle></DialogHeader>
      <DialogCloseTrigger disabled={isSubmitting} />
      <form noValidate onSubmit={handleSubmit(submit)}>
        <DialogBody><Stack gap="4">
          {error && <Alert role="alert" status="error" title={error} />}
          {removing ? <Text>{t(started ? "customHolidays.endConfirmation" : "customHolidays.deleteConfirmation", { name: rule!.name })}</Text> : <>
            <Field id="holiday-name" label={t("customHolidays.name")} required>
              <Input id="holiday-name" {...register("name")} maxLength={200} disabled={isSubmitting} />
            </Field>
            <Field id="holiday-type" label={t("customHolidays.ruleType")}>
              <NativeSelect.Root disabled={isSubmitting}><NativeSelect.Field id="holiday-type" {...register("type")}>
                <option value="" disabled>{t("customHolidays.ruleType")}</option>
                {ruleTypes.map(value => <option key={value} value={value}>{t(`customHolidays.${value}`)}</option>)}
              </NativeSelect.Field><NativeSelect.Indicator /></NativeSelect.Root>
            </Field>
            {type === "once" && <DateSelectController {...dateProps} name="date" label={t("publicHolidays.date")} clearable={false} disabled={isSubmitting} />}
            {(type === "annual_date" || type === "nth_weekday") && <SimpleGrid columns={{ base: 1, sm: type === "annual_date" ? 2 : 3 }} gap="3">
              <Field id="holiday-month" label={t("customHolidays.month")}>
                <NativeSelect.Root disabled={isSubmitting}><NativeSelect.Field id="holiday-month" {...register("month")}>
                  {Array.from({ length: 12 }, (_, i) => <option value={i + 1} key={i}>{new Date(2000, i, 1).toLocaleDateString(locale, { month: "long" })}</option>)}
                </NativeSelect.Field><NativeSelect.Indicator /></NativeSelect.Root>
              </Field>
              {type === "annual_date" ? <Field id="holiday-day" label={t("customHolidays.day")}>
                <NativeSelect.Root disabled={isSubmitting}><NativeSelect.Field id="holiday-day" {...register("day")}>
                  {Array.from({ length: 31 }, (_, i) => <option value={i + 1} key={i}>{i + 1}</option>)}
                </NativeSelect.Field><NativeSelect.Indicator /></NativeSelect.Root>
              </Field> : <>
                <Field id="holiday-weekday" label={t("publicHolidays.weekday")}>
                  <NativeSelect.Root disabled={isSubmitting}><NativeSelect.Field id="holiday-weekday" {...register("weekday")}>
                    {Array.from({ length: 7 }, (_, i) => <option value={i + 1} key={i}>{new Date(2024, 0, i + 1).toLocaleDateString(locale, { weekday: "long" })}</option>)}
                  </NativeSelect.Field><NativeSelect.Indicator /></NativeSelect.Root>
                </Field>
                <Field id="holiday-ordinal" label={t("customHolidays.ordinal")}>
                  <NativeSelect.Root disabled={isSubmitting}><NativeSelect.Field id="holiday-ordinal" {...register("ordinal")}>
                    {[1, 2, 3, 4, 5].map(value => <option value={value} key={value}>{t(`publicHolidays.ordinal${value}`)}</option>)}
                  </NativeSelect.Field><NativeSelect.Indicator /></NativeSelect.Root>
                </Field>
              </>}
            </SimpleGrid>}
            <Field id="holiday-duration" label={t("customHolidays.duration")}>
              <NativeSelect.Root disabled={isSubmitting}><NativeSelect.Field id="holiday-duration" {...register("duration")}>
                <option value="1">{t("publicHolidays.fullDay")}</option>
                <option value="0.5">{t("publicHolidays.halfDay")}</option>
              </NativeSelect.Field><NativeSelect.Indicator /></NativeSelect.Root>
            </Field>
          </>}
          {(!removing || started) && <DateSelectController {...dateProps} name="from" clearable={false} disabled={isSubmitting}
            label={t(started ? "customHolidays.effectiveDate" : "customHolidays.validFrom")} />}
          {!removing && <DateSelectController {...dateProps} minDate={new Date(`${watch("from") || tomorrow}T00:00:00`)} name="until"
            label={t("customHolidays.validUntil")} clearable disabled={isSubmitting} />}
          {started && <Text fontSize="sm" color="fg.muted">{t(removing ? "customHolidays.endHistory" : "customHolidays.editHistory")}</Text>}
        </Stack></DialogBody>
        <DialogFooter>
          <CancelButton onClick={onClose} disabled={isSubmitting} />
          <SubmitButton isSubmitting={isSubmitting} colorPalette={removing ? "red" : "blue"}
            caption={removing ? t(started ? "customHolidays.end" : "customHolidays.remove") : t("common.save")} />
        </DialogFooter>
      </form>
    </DialogContent>
  </DialogRoot>;
};
